import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TypeBadge, SourceBadge, ScopeBadge } from '../channels/channel-badges.jsx';
import { api, ApiError } from '../../api/index.js';

/**
 * Inline panel inside PersonDetail listing every channel the person belongs
 * to — both via OSS hierarchy (derived) and via explicit custom membership.
 */
export function PersonChannelsSection({ personId }) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!personId) return;
    let cancelled = false;
    setLoading(true);
    api.channels
      .forPerson(personId)
      .then((r) => { if (!cancelled) setChannels(r.channels || []); })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [personId]);

  return (
    <>
      <div className="border-t border-line bg-canvas/40 px-6 py-3 text-[11px] font-semibold uppercase tracking-wider text-mist">
        Channels ({loading ? '…' : channels.length})
      </div>
      {error ? (
        <div className="px-6 py-4 text-sm text-rose-700">{error}</div>
      ) : channels.length === 0 ? (
        <div className="px-6 py-4 text-sm text-mist">
          {loading ? (
            <><i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden="true" /> Loading…</>
          ) : (
            'Not a member of any channel yet.'
          )}
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {channels.map((c) => (
            <li key={c.id} className="px-6 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to="/channels"
                  className="font-mono text-[12px] font-semibold text-ink hover:text-accent"
                  title={c.name}
                >
                  {c.path}
                </Link>
                <TypeBadge type={c.type} />
                <SourceBadge source={c.source} />
                {c.source === 'oss' && <ScopeBadge scope={c.scope} />}
                {c.role && c.source === 'custom' && (
                  <span className="rounded-full border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-mist">
                    {c.role}
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-[11px] text-mist">{c.name}</div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
